import React, { useState, useEffect } from 'react';
import IDDocumentTypePicker from '../sections/components/IDDocumentTypePicker.jsx';
import DocumentSlotCapture from '../sections/components/DocumentSlotCapture.jsx';
import { hasUnacknowledgedReview } from '../util/documentProblems.js';

// Owns the type-picker → slot-capture sub-flow shared by IDCardSection ("identity task") and
// GuidedIdSection ("simple task"). The PARENT still owns its SectionNav; this hook only exposes the
// current sub-screen, the node to render, whether Next is allowed, and a goBack() that reports whether
// it consumed the Back press (i.e. stayed inside the guided flow).
export function useIdDocumentCapture({
	acceptedTypes, // e.g. ['id','passport'] or ['passport']
	document, // the already-captured doc (value[0]); drives canContinue + re-derive
	onDocument, // (file|null) => void — parent persists the captured document
	text,
	uploadUrl,
	context,
	reviewMessages,
	useCamera = true,
	forceCamera = false,
	cameraIncludes,
	cameraRotationAngle,
	disabled = false,
	addPendingAction = () => {},
	removePendingAction = () => {},
}) {
	const single = acceptedTypes.length === 1;
	const [selectedType, setSelectedType] = useState(single ? acceptedTypes[0] : null);
	const [subScreen, setSubScreen] = useState(single ? 'capture' : 'type');

	// Returning user: re-derive the pick from an already-captured document so canContinue works.
	// Deps: stored type ONLY. Adding selectedType would re-fire after goBack()'s reset and undo it
	// (this is the exact hazard IDCardSection's original effect warned about).
	const storedType = document?.type;
	useEffect(() => {
		if (storedType && storedType !== selectedType) setSelectedType(storedType);
	}, [storedType]);

	const selectType = (t) => {
		setSelectedType(t);
		setSubScreen('capture');
	};

	// capture → type. Clears the pick ONLY when there's a real picker to return to (>1 type),
	// mirroring IDCardSection's ">1" guard so a single-type flow keeps its auto-selected type.
	const goBack = () => {
		if (subScreen !== 'capture') return false;
		if (!single) setSelectedType(null);
		setSubScreen('type');
		return true;
	};

	const canContinue =
		subScreen === 'type'
			? !!selectedType
			: !!document && !hasUnacknowledgedReview(document ? [document] : []);

	const node =
		subScreen === 'type' ? (
			<IDDocumentTypePicker acceptedDocuments={acceptedTypes} text={text} onSelect={selectType} />
		) : (
			<DocumentSlotCapture
				type={selectedType}
				allowedTypes={[selectedType]}
				text={text}
				context={context}
				uploadUrl={uploadUrl}
				reviewMessages={reviewMessages}
				useCamera={useCamera}
				forceCamera={forceCamera}
				cameraIncludes={cameraIncludes}
				cameraRotationAngle={cameraRotationAngle}
				disabled={disabled}
				addPendingAction={addPendingAction}
				removePendingAction={removePendingAction}
				onComplete={onDocument}
			/>
		);

	return { subScreen, node, canContinue, goBack, selectedType };
}
